/**
 * Google sign-in through Firebase, used for exactly one thing: proving to our
 * own server who someone is.
 *
 * Firebase never becomes the session. It hands back a signed ID token, the API
 * verifies it and sets the same httpOnly cookie a password login gets, and then
 * Firebase is signed out again. Keeping one kind of session means one place to
 * revoke it, one place that decides roles, and no token sitting in JavaScript
 * where a script could read it. Persistence is in-memory for the same reason:
 * nothing Firebase knows should outlive the popup.
 *
 * The SDK is a few hundred kilobytes that only the login screen ever needs, so
 * it is imported on demand and stays out of the bundle everyone else loads.
 */
import type { AuthConfig, SessionInfo } from "@scandi/shared";
import type { Auth } from "firebase/auth";
import { api } from "@/lib/api";

export type GoogleConfig = NonNullable<AuthConfig["google"]>;

/**
 * Why a Google sign-in did not happen, for failures that are Firebase's rather
 * than our server's (those arrive as an ApiError with a code).
 *
 * - `popup_blocked`: the browser refused to open the window; the reader can
 *   allow popups and try again.
 * - `failed`: anything else Firebase reported. Its own message is kept for the
 *   console, not shown: it is English and written for developers.
 */
export class GoogleSignInError extends Error {
  readonly reason: "popup_blocked" | "failed";

  constructor(reason: GoogleSignInError["reason"], message: string) {
    super(message);
    this.name = "GoogleSignInError";
    this.reason = reason;
  }
}

type FirebaseModules = [typeof import("firebase/app"), typeof import("firebase/auth")];

let modules: Promise<FirebaseModules> | null = null;
let authInstance: { key: string; auth: Auth } | null = null;

/**
 * Starts downloading the SDK without using it.
 *
 * Called by the login screen as soon as it knows Google sign-in is on. Browsers
 * only let a page open a popup shortly after a click, and Safari's window is
 * short: fetching several hundred kilobytes between the click and the popup
 * would get it blocked. Loaded ahead, the click finds everything ready.
 */
export function preloadFirebase(): Promise<FirebaseModules> {
  modules ??= Promise.all([import("firebase/app"), import("firebase/auth")]);
  // A failed download (flaky connection) must not stick for the page's lifetime.
  modules.catch(() => {
    modules = null;
  });
  return modules;
}

/**
 * Gets Google sign-in fully ready before anyone clicks.
 *
 * Downloading the SDK is not enough. On the first sign-in the popup resolver
 * still has to load Google's gapi script and the authDomain's hidden iframe,
 * over the network, before it calls window.open. On Safari and iOS that costs
 * more than the short allowance a click grants, so the first popup was blocked
 * and only a second click worked. Creating the Auth instance here starts that
 * work now (initializeAuth begins it straight away on those browsers), and the
 * click then finds it cached and opens the window at once.
 */
export function prepareGoogleSignIn(config: GoogleConfig): Promise<void> {
  return getFirebaseAuth(config).then(({ auth }) => auth.authStateReady());
}

async function getFirebaseAuth(config: GoogleConfig): Promise<{ auth: Auth; sdk: FirebaseModules[1] }> {
  const [app, sdk] = await preloadFirebase();
  const key = `${config.projectId}:${config.apiKey}`;

  if (!authInstance || authInstance.key !== key) {
    const name = `scandi-${key}`;
    // Reuse an app a failed earlier attempt left behind (initializeAuth can
    // throw after initializeApp succeeded), or the retry dies on "already exists".
    const firebaseApp =
      app.getApps().find((existing) => existing.name === name) ??
      app.initializeApp(
        {
          apiKey: config.apiKey,
          authDomain: config.authDomain,
          projectId: config.projectId,
          ...(config.appId ? { appId: config.appId } : {}),
        },
        // Named after the project so a config change (rare, but a redeploy can do
        // it under an open tab) gets a fresh app instead of "already exists".
        name,
      );
    // initializeAuth rather than getAuth + setPersistence: getAuth starts with
    // IndexedDB persistence and would touch storage before being told not to.
    const auth = sdk.initializeAuth(firebaseApp, {
      persistence: sdk.inMemoryPersistence,
      popupRedirectResolver: sdk.browserPopupRedirectResolver,
    });
    authInstance = { key, auth };
  }

  return { auth: authInstance.auth, sdk };
}

/** Firebase codes that mean the person changed their mind, not that anything broke. */
const CANCELLED = new Set(["auth/popup-closed-by-user", "auth/cancelled-popup-request", "auth/user-cancelled"]);

/**
 * Opens the Google account picker and signs in to the portal with the result.
 *
 * Resolves to the new session, or to null when the popup was closed without
 * choosing an account. Rejects with an ApiError when our server refused the
 * account (disabled, unverified email), or a GoogleSignInError when Firebase
 * could not get that far.
 */
export async function signInWithGoogle(config: GoogleConfig): Promise<SessionInfo | null> {
  const { auth, sdk } = await getFirebaseAuth(config);

  const provider = new sdk.GoogleAuthProvider();
  // Always show the picker. Shared computers and people with a personal and a
  // work account would otherwise be signed in as whoever Google last saw.
  provider.setCustomParameters({ prompt: "select_account" });

  let idToken: string;
  try {
    const credential = await sdk.signInWithPopup(auth, provider);
    idToken = await credential.user.getIdToken();
  } catch (error) {
    const code = (error as { code?: unknown }).code;
    if (typeof code === "string" && CANCELLED.has(code)) {
      console.warn("Google sign-in closed without a result", code);
      return null;
    }
    if (code === "auth/popup-blocked") {
      throw new GoogleSignInError("popup_blocked", String(error));
    }
    if (code === "auth/network-request-failed") {
      // Reported the same way as our own server being unreachable.
      throw new TypeError(String(error));
    }
    console.error("Google sign-in failed", error);
    throw new GoogleSignInError("failed", String(error));
  }

  try {
    return await api.googleSignIn(idToken);
  } finally {
    // Our cookie is the session now, or the server said no; either way Firebase
    // has nothing left to remember.
    await sdk.signOut(auth).catch(() => undefined);
  }
}
